/**
 * Kept in its own module because shared/rbac.ts and shared/synthetic-agents.ts
 * import each other; a const read across that cycle at load time would be in
 * its temporal dead zone.
 */

/**
 * #1835 slice 12: an entry in `allowed_table_prefixes` that starts with "="
 * grants ONE table by exact name instead of a family by prefix, so
 * `=production_monitor_batch` reaches production_monitor_batch and never
 * production_monitor_batchmaterial.
 *
 * Code only (#4307 review): the mark is honoured ONLY in a list minted by
 * codeDefinedGrants, which syntheticAgentAccess uses for the profiles in
 * shared/synthetic-agents.ts. A list read from D1 or an admin edit is a plain
 * array, so a "=" entry there keeps matching nothing, as it did before this
 * change: no stored role can widen on deploy. A copy of a minted list (spread,
 * JSON round trip, structured clone) is plain too and also matches nothing for
 * its "=" entries, so losing the mark fails closed. Minted lists are frozen.
 */
export const EXACT_TABLE_MARK = "=";

const CODE_DEFINED_GRANT_LISTS = new WeakSet<readonly string[]>();

/** The `allowed_table_prefixes` entry that grants exactly one table (honoured only inside codeDefinedGrants). */
export function exactTableGrant(table: string): string {
  return `${EXACT_TABLE_MARK}${table.toLowerCase()}`;
}

/** A frozen copy of `list` whose "=" entries isTableNameAllowed honours. Code-defined profiles only. */
export function codeDefinedGrants(list: readonly string[]): string[] {
  const minted = Object.freeze([...list]);
  CODE_DEFINED_GRANT_LISTS.add(minted);
  return minted as string[];
}

/** True only for a list minted by codeDefinedGrants. */
export function honoursExactGrants(list: readonly string[]): boolean {
  return CODE_DEFINED_GRANT_LISTS.has(list);
}
